import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useEntityPage } from 'c-hooks';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import type { SystemSearchEntityResource } from 'c-system-search';
import ReactGA from 'react-ga';
import { campaignIcon } from 'c-main/icons';
import Sidebar from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar';
import ConversionMeasurementTest1234 from 'c-main/Components/Campaign/CampaignReportingWrapper/ConversionMeasurement/ConversionMeasurementTest1234';
import { generatePath, useHistory } from 'react-router-dom';

const ALLOWED_CONVERSION_MEASUREMENT_CAMPAIGN_IDS = [12907, 13191];

const CampaignConversionMeasurementTest1234Page = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);

    const { id } = useEntityPage();
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const toggleMobileDrawer = useCallback(() => setMobileDrawerOpen(p => !p), []);
    const isAllowedCampaign = ALLOWED_CONVERSION_MEASUREMENT_CAMPAIGN_IDS.includes(Number(id));

    useEffect(() => {
        if (id == null || isAllowedCampaign) return;

        history.replace(generatePath(PostAuthRoutes.Campaigns.CampaignList));
    }, [history, id, isAllowedCampaign]);

    if (id != null && !isAllowedCampaign) return null;

    return isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'grey.300',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: 'background.paper',
                }}
            >
                <IconButton onClick={toggleMobileDrawer} edge="start" aria-label="menu">
                    <MenuIcon />
                </IconButton>
            </Box>
            <Sidebar
                id={id}
                mobileDrawerOpen={mobileDrawerOpen}
                toggleMobileDrawer={toggleMobileDrawer}
            />
            <Box sx={{ flexGrow: 1, px: 2, pt: 2 }}>
                <ConversionMeasurementTest1234 />
            </Box>
        </Box>
    ) : (
        <Box sx={{ display: 'flex' }}>
            <Sidebar id={id} />
            <Box sx={{ flexGrow: 1, pt: 2, px: 2 }}>
                <ConversionMeasurementTest1234 />
            </Box>
        </Box>
    );
};

const PageBreadcrumbs: SystemSearchEntityResource<Campaign>['renderEntityBreadcrumbs'] = () => (
    <Box display="flex" />
);

const setup: PageEntityConfig<Campaign> = {
    id: 'CampaignConversionMeasurementTest1234Page',
    component: CampaignConversionMeasurementTest1234Page,
    systemSearch: {
        title: undefined,
        description: 'Modules.Main.Campaigns.Overview.description',
        icon: campaignIcon,
        type: 'entity',
        aliases: undefined,
        route: PostAuthRoutes.Campaigns.CampaignConversionMeasurementTest1234,
        saveAsRecentPage: true,
        entityName: 'Campaign',
        defaultIncludes: [
            'agency',
            'market',
            'creatives',
            'client',
            'creatives.resolution',
            'creatives.file',
            'budgetSummary',
            'budgetSummary.lineItems',
            'budgetSummaries',
            'budgetSummaries.lineItems',
            'notices',
            'errors',
            'creativeThumbnails',
            'bookingSummaries',
            'popShotsCount',
            'missingDisplaysCount',
            'happydemics',
            'additionalContacts',
        ],
        prefix: 'SystemSearch.prefixes.campaign',
        prefixDescription: 'SystemSearch.prefixDescriptions.campaign',
        breadcrumbs: [
            {
                label: 'Modules.Main.Campaigns.List.metaTitle',
                link: PostAuthRoutes.Campaigns.CampaignList,
            },
        ],
        nameGen: entity => `${entity?.name}-${entity?.id}`,
        permissions: [PermissionName.UniledportalCampaignRead],
        renderEntityBreadcrumbs: PageBreadcrumbs,
    },
};

export default setup;
